import React from "react";
import BondMark from "./BondMark";

/**
 * BondTicker — two slow, infinite marquees of illustrative bond cards.
 *
 * Each card: peer A · scenario · stake · peer B. Tagged as illustrative so it
 * never reads as a real live feed. Top row drifts left, bottom row drifts
 * right, creating a quiet sense of motion under the hero.
 */

// Illustrative P2P bonds between Rwandan participants.
const BONDS = [
  { a: "Patrick", b: "Aline", scenario: "Arsenal beat City", stake: 2000 },
  { a: "Eric", b: "Diane", scenario: "APR win the league", stake: 1500 },
  { a: "Sandrine", b: "Olivier", scenario: "Real Madrid lift La Liga", stake: 500 },
  { a: "Jean", b: "Yvette", scenario: "Volcanoes win the Cup", stake: 3500 },
  { a: "Aimable", b: "Claude", scenario: "France win Six Nations", stake: 1000 },
  { a: "Bosco", b: "Honorine", scenario: "Sub-4h Kigali marathon", stake: 2500 },
  { a: "Diane", b: "Sonia", scenario: "PSG take Ligue 1", stake: 750 },
  { a: "Felix", b: "Patrick", scenario: "Rayon Sport at home", stake: 1500 },
  { a: "Aline", b: "Eric", scenario: "Bayern keep the Bundesliga", stake: 1000 },
  { a: "Olivier", b: "Yvette", scenario: "Inter win Serie A", stake: 2000 },
];

const formatCash = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);

const BondCard = ({ bond }) => (
  <div className="mx-2 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 shadow-[0_18px_60px_-34px_rgba(30,136,229,0.8)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1E88E5]/45 hover:bg-[#1E88E5]/[0.045]">
    <span className="flex items-center gap-2">
      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-white/15 to-white/[0.03] flex items-center justify-center text-[10px] font-bold text-white">
        {bond.a.charAt(0)}
      </span>
      <span className="text-gray-300 text-xs font-medium">{bond.a}</span>
    </span>
    <BondMark size={14} />
    <span className="flex items-center gap-2">
      <span className="text-gray-300 text-xs font-medium">{bond.b}</span>
      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-white/15 to-white/[0.03] flex items-center justify-center text-[10px] font-bold text-white">
        {bond.b.charAt(0)}
      </span>
    </span>
    <span className="h-1 w-1 rounded-full bg-[#1E88E5]/45" />
    <span className="text-gray-400 text-xs whitespace-nowrap">
      “{bond.scenario}”
    </span>
    <span className="h-1 w-1 rounded-full bg-[#1E88E5]/45" />
    <span className="rounded-full border border-[#1E88E5]/35 bg-[#1E88E5]/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-[#64B5F6]">
      {formatCash(bond.stake)} each
    </span>
  </div>
);

const Row = ({ bonds, direction = "left", duration = 60 }) => {
  // Duplicate to make the marquee loop seamlessly
  const items = [...bonds, ...bonds];
  return (
    <div className="overflow-hidden mask-fade">
      <div
        className="flex whitespace-nowrap will-change-transform"
        style={{
          animation: `bond-marquee-${direction} ${duration}s linear infinite`,
        }}
      >
        {items.map((bond, i) => (
          <BondCard key={`${bond.a}-${bond.b}-${i}`} bond={bond} />
        ))}
      </div>
    </div>
  );
};

const BondTicker = () => {
  const topRow = BONDS;
  const bottomRow = [...BONDS].reverse();

  return (
    <section className="relative overflow-hidden bg-black py-14 border-t border-white/5">
      <div
        className="absolute left-1/2 top-0 h-64 w-[70%] -translate-x-1/2 rounded-full bg-[#1E88E5]/10 blur-[100px]"
        aria-hidden
      />
      <div className="container relative mx-auto px-4">
        <p className="mb-6 text-center text-[10px] uppercase tracking-[0.26em] text-gray-600">
          Illustrative P2P bonds
        </p>
        <div className="space-y-3">
          <Row bonds={topRow} direction="left" duration={70} />
          <Row bonds={bottomRow} direction="right" duration={85} />
        </div>
      </div>

      <style>{`
        @keyframes bond-marquee-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes bond-marquee-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        .mask-fade {
          -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
                  mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
        }
        @media (prefers-reduced-motion: reduce) {
          .mask-fade > div { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default BondTicker;
